import { ArrowRight, HardDrive, Terminal, Check, X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '../../button';

import { executionTrace, initialTasks } from './collabboard-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect as SelectField } from './learning-model-surface';

import type { TaskRecord } from './collabboard-models';

export function RepresentationLab() {
  const [design, setDesign] = useState<'parallel' | 'records'>('parallel');
  const [tasks, setTasks] = useState(initialTasks);
  const [selected, setSelected] = useState('2');
  const [acted, setActed] = useState(false);
  const reset = () => {
    setTasks(initialTasks);
    setSelected('2');
    setActed(false);
  };
  const mismatch =
    design === 'parallel' &&
    tasks.some((task, index) => task.completed !== initialTasks[index]?.completed);
  return (
    <Lab title="What holds a task together?" eyebrow="01 / Representation">
      <div className="cb-tabs" aria-label="Data representation">
        <Button
          variant="secondary"
          size="sm"
          aria-pressed={design === 'parallel'}
          onClick={() => {
            setDesign('parallel');
            reset();
          }}
        >
          Two parallel lists
        </Button>
        <Button
          variant="secondary"
          size="sm"
          aria-pressed={design === 'records'}
          onClick={() => {
            setDesign('records');
            reset();
          }}
        >
          A list of records
        </Button>
      </div>
      <div className="cb-record-grid" data-design={design}>
        <div className="cb-record-heading">
          <span>{design === 'parallel' ? 'tasks[i]' : 'One task record'}</span>
          <span>{design === 'parallel' ? 'completed[i]' : 'Fields stay together'}</span>
        </div>
        {(design === 'parallel' ? initialTasks : tasks).map((original, index) => {
          const task = design === 'parallel' ? tasks[index] : original;
          const done = original.completed;
          const wrong = design === 'parallel' && task !== undefined && done !== task.completed;
          return (
            <div className="cb-record-row" data-wrong={wrong || !task} key={original.id}>
              <span className="cb-record-title">
                <small>{design === 'parallel' ? `[${index}]` : `id: ${original.id}`}</small>
                {task?.title ?? 'No title at this position'}
              </span>
              <ArrowRight size={16} aria-hidden="true" />
              <span className="cb-record-value">
                {done ? <Check size={14} aria-hidden="true" /> : <X size={14} aria-hidden="true" />}{' '}
                {done ? 'True' : 'False'}
                {wrong && <small>Wrong task</small>}
                {!task && <small>Left over</small>}
              </span>
            </div>
          );
        })}
      </div>
      <div className="cb-controls">
        <label>
          Task to delete
          <SelectField
            value={selected}
            onValueChange={setSelected}
            disabled={tasks.length === 0}
            aria-label="Task to delete"
            options={tasks.map((task) => ({ value: String(task.id), label: task.title }))}
          />
        </label>
        <Button
          size="sm"
          disabled={tasks.length === 0}
          onClick={() => {
            const remaining = tasks.filter((task) => task.id !== Number(selected));
            setTasks(remaining);
            setSelected(String(remaining[0]?.id ?? ''));
            setActed(true);
          }}
        >
          Delete selected task
        </Button>
        <Reset onClick={reset} />
      </div>
      <code className="cb-code-line">
        {design === 'parallel'
          ? 'del tasks[index]  # completed was not changed'
          : 'del tasks[index]  # removes the whole record'}
      </code>
      <Result tone={!acted ? 'neutral' : design === 'records' ? 'success' : 'warning'}>
        {!acted
          ? 'Predict the remaining title–completion pairs, then delete one task.'
          : design === 'records'
            ? 'The remaining records keep their own completion values and IDs. Position changed; identity did not.'
            : mismatch
              ? 'The two lists now disagree: a completion value has moved onto a different task. Both lists are individually valid.'
              : 'There is a completion value with no matching title. The lengths no longer agree, even if the remaining pairs look right.'}
      </Result>
    </Lab>
  );
}

const programLines = [
  'open_count = 0',
  'for task in tasks:',
  '    if not task["completed"]:',
  '        open_count += 1',
  '        print(task["title"])',
  'print("Open:", open_count)',
];

export function ExecutionLab() {
  const [tasks, setTasks] = useState(initialTasks);
  const [position, setPosition] = useState(-1);
  const trace = executionTrace(tasks);
  const step = trace[position];
  return (
    <Lab title="Follow one instruction at a time" eyebrow="02 / Execution">
      <div className="cb-checks" aria-label="Starting task completion">
        {tasks.map((task) => (
          <label key={task.id}>
            <input
              type="checkbox"
              checked={task.completed}
              onChange={() => {
                setTasks(
                  tasks.map((item) =>
                    item.id === task.id ? { ...item, completed: !item.completed } : item,
                  ),
                );
                setPosition(-1);
              }}
            />
            {task.title} is completed
          </label>
        ))}
      </div>
      <div className="cb-two">
        <Panel label="Python instructions">
          <ol className="cb-program">
            {programLines.map((line, index) => (
              <li key={line} aria-current={step?.line === index ? 'step' : undefined}>
                <span>{index + 1}</span>
                <code>{line}</code>
              </li>
            ))}
          </ol>
        </Panel>
        <Panel label="Process memory">
          <dl className="cb-memory">
            <div>
              <dt>open_count</dt>
              <dd>{step?.count ?? 'not assigned'}</dd>
            </div>
            <div>
              <dt>task</dt>
              <dd>{step?.task ? `#${step.task.id} · ${step.task.title}` : 'not assigned'}</dd>
            </div>
            <div>
              <dt>task["completed"]</dt>
              <dd>{step?.task ? (step.task.completed ? 'True' : 'False') : '—'}</dd>
            </div>
          </dl>
          <div className="cb-terminal">
            <span>Output</span>
            <pre>
              {step && step.output.length > 0 ? step.output.join('\n') : 'Nothing printed yet'}
            </pre>
          </div>
        </Panel>
      </div>
      <div className="cb-controls">
        <Button
          size="sm"
          disabled={position >= trace.length - 1}
          onClick={() => setPosition(position + 1)}
        >
          Execute next line
        </Button>
        <Reset onClick={() => setPosition(-1)} />
        <span>
          {position + 1} / {trace.length} steps
        </span>
      </div>
      <Result>
        {step?.why ?? 'Change a starting value, then step. The highlighted line has just executed.'}
      </Result>
    </Lab>
  );
}

interface Snapshot {
  next_id: number;
  tasks: TaskRecord[];
}
const emptySnapshot = (): Snapshot => ({ next_id: 1, tasks: [] });
const startingSnapshot = (): Snapshot => ({
  next_id: 2,
  tasks: [{ id: 1, title: 'Buy milk', completed: false }],
});

export function PersistenceLab() {
  const [memory, setMemory] = useState<Snapshot | null>(startingSnapshot);
  const [disk, setDisk] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const [message, setMessage] = useState('One task is in memory. There is no saved file yet.');
  const [warning, setWarning] = useState(false);
  function load() {
    try {
      const parsed: unknown = disk ? JSON.parse(disk) : emptySnapshot();
      if (
        !parsed ||
        typeof parsed !== 'object' ||
        !('tasks' in parsed) ||
        !Array.isArray(parsed.tasks) ||
        !('next_id' in parsed) ||
        typeof parsed.next_id !== 'number'
      )
        throw new Error('Invalid state');
      // Only known snapshots generated by this model, or the explicit malformed case, enter here.
      setMemory(parsed as Snapshot);
      setWarning(false);
      setMessage(
        disk
          ? 'Loaded the saved snapshot. Unsaved changes from the old process are absent.'
          : 'A new process starts empty. The previous process’s memory was reclaimed.',
      );
    } catch {
      setMemory(null);
      setWarning(true);
      setMessage(
        'The file could not be parsed. Startup stops and the file stays intact; it is not silently replaced with empty state.',
      );
    }
  }
  return (
    <Lab title="Which copy survives a restart?" eyebrow="03 / Persistence">
      <div className="cb-transfer-layout">
        <Panel label="Process memory">
          <div className="cb-panel-icon">
            <Terminal size={19} />
            <span>{memory ? 'Running' : 'Stopped'}</span>
          </div>
          {memory ? (
            <>
              <small>next_id = {memory.next_id}</small>
              <ul className="cb-task-stack">
                {memory.tasks.length ? (
                  memory.tasks.map((task) => (
                    <li key={task.id}>
                      <small>#{task.id}</small>
                      {task.title}
                    </li>
                  ))
                ) : (
                  <li>tasks = []</li>
                )}
              </ul>
            </>
          ) : (
            <p>No application state in RAM</p>
          )}
        </Panel>
        <div className="cb-transfer">
          <span>serialize</span>
          <ArrowRight size={22} aria-hidden="true" />
          <code>UTF-8 bytes</code>
          <span>parse on load</span>
        </div>
        <Panel label="tasks.json on disk">
          <div className="cb-panel-icon">
            <HardDrive size={19} />
            <span>{disk ? `${new TextEncoder().encode(disk).length} bytes` : 'No file'}</span>
          </div>
          <pre className="cb-json">{disk ?? 'No snapshot has been written.'}</pre>
        </Panel>
      </div>
      <div className="cb-controls">
        <Button
          variant="secondary"
          size="sm"
          disabled={!memory}
          onClick={() => {
            if (!memory) return;
            setMemory({
              next_id: memory.next_id + 1,
              tasks: [
                ...memory.tasks,
                { id: memory.next_id, title: `New task ${memory.next_id}`, completed: false },
              ],
            });
            setWarning(false);
            setMessage('Only process memory changed. The saved snapshot is still the same.');
          }}
        >
          Add task in memory
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!memory}
          onClick={() => {
            if (!memory) return;
            if (denied) {
              setWarning(true);
              setMessage('Write rejected. Disk is unchanged; the new task is still only in RAM.');
              return;
            }
            setDisk(JSON.stringify(memory, null, 2));
            setWarning(false);
            setMessage('Serialized and saved. This snapshot can outlive the running process.');
          }}
        >
          Save snapshot
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            if (memory) {
              setMemory(null);
              setWarning(false);
              setMessage('Process stopped. Its memory is gone; the disk snapshot did not change.');
            } else load();
          }}
        >
          {memory ? 'Stop process' : 'Start process'}
        </Button>
        <Button variant="secondary" size="sm" disabled={!memory || disk === null} onClick={load}>
          Load saved snapshot
        </Button>
      </div>
      <div className="cb-controls cb-secondary">
        <label className="cb-check">
          <input type="checkbox" checked={denied} onChange={(e) => setDenied(e.target.checked)} />
          Disk rejects writes
        </label>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            setDisk('{broken');
            setMessage('The simulated file now contains malformed JSON. Try loading it.');
            setWarning(true);
          }}
        >
          Simulate damaged file
        </Button>
        <Reset
          onClick={() => {
            setMemory(startingSnapshot());
            setDisk(null);
            setDenied(false);
            setWarning(false);
            setMessage('One task is in memory. There is no saved file yet.');
          }}
        />
      </div>
      <Result tone={warning ? 'warning' : 'neutral'}>{message}</Result>
    </Lab>
  );
}
